import { useMutation, useQuery, useQueryClient } from "react-query";
import qs from "qs-lite";

import api from "../../utils/api";
import { useHubSnackBar } from "../useSnackBar";
import { getQueryParams } from "../../helpers";
import { invalidate } from "../../helpers/clientHelper";

const SHIPMENTS = "/shipments";
const CACHE_TIME = 30_600_000;

const updateShipment = (payload) => {
  const id = payload.id;
  delete payload.id;
  return api.put(`${SHIPMENTS}/${id}`, payload);
};
export const useUpdateShipment = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(updateShipment, {
    onSuccess: () => {
      invalidate(queryClient, SHIPMENTS);
      addSnackbarSuccess({ message: "Successfully Updated Shipment" });
    },
  });
};

const updateShipmentPackages = (payload) => {
  const id = payload.id;
  delete payload.id;
  return api.put(`${SHIPMENTS}/packages/${id}`, payload);
};
export const useUpdateShipmentPackages = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(updateShipmentPackages, {
    onSuccess: () => {
      invalidate(queryClient, SHIPMENTS);
      addSnackbarSuccess({ message: "Successfully Updated Shipment" });
    },
  });
};

const postShipment = (payload) => api.post(SHIPMENTS, payload);
export const usePostShipment = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postShipment, {
    onSuccess: () => {
      console.log("Invalidating...");
      invalidate(queryClient, SHIPMENTS);
      addSnackbarSuccess({ message: "Successfully Created Shipment" });
    },
  });
};

const getShipmentLists = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${SHIPMENTS}/list?${queryString}`);
};
export const useGetShipmentLists = ({ enabled = true, locationId = 1 } = {}) => {
  return useQuery([SHIPMENTS, { locationId }], getShipmentLists, {
    enabled: !!enabled,
    staleTime: CACHE_TIME,
    cacheTime: CACHE_TIME,
  });
};

const getAllShipmentOrders = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${SHIPMENTS}?${queryString}`);
};
export const useGetAllShipmentOrders = ({
  locationId = 1,
  returning,
  customerId,
  orderId,
  enabled,
}) => {
  let include = [
    ["location", locationId],
    ["returning", returning],
  ];

  if (customerId) include = [["customerId", customerId]];
  if (orderId) include = [["orderId", orderId]];

  const queryProps = getQueryParams({
    defaultOrderBy: "createdAt",
    defaultSortOrder: "desc",
    include,
  });

  return useQuery([SHIPMENTS, { ...queryProps }], getAllShipmentOrders, {
    enabled,
  });
};

const getShipmentDetails = (id) => api.get(`${SHIPMENTS}/${id}`);
export const useGetShipmentDetails = (id) => {
  const enabled = typeof id != "boolean" && !isNaN(Number(id));
  return useQuery([SHIPMENTS, id], () => getShipmentDetails(id), {
    enabled,
  });
};

const getShipmentPackagesDetails = ({ queryKey }) => {
  const [_, id] = queryKey;
  return api.get(`${SHIPMENTS}/boxing/${id}`);
};
export const useGetShipmentPackagesDetails = (id) => {
  const enabled = typeof id != "boolean" && !isNaN(Number(id));
  return useQuery([SHIPMENTS, id], getShipmentPackagesDetails, {
    enabled,
  });
};

const getOrderShipmentsById = ({ queryKey }) => {
  const id = queryKey[2];
  return api.get(`${SHIPMENTS}/order/${id}`);
};
export const useGetOrderShipmentsById = (id, open) => {
  return useQuery([SHIPMENTS, "/order", id], getOrderShipmentsById, {
    enabled: !!id && open,
  });
};

const getLastBoxes = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${SHIPMENTS}/last-boxes?${queryString}`);
};
export const useGetLastShipmentBoxes = ({ customerId, stationId, returning }) =>
  useQuery(
    [SHIPMENTS, { customerId, stationId, returning }, "last-boxes"],
    getLastBoxes,
    { enabled: !!(customerId || stationId) },
  );

const deleteShipmentById = (id) => api.delete(`${SHIPMENTS}/${id}`);
export const useDeleteShipmentById = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deleteShipmentById, {
    onSuccess: () => {
      invalidate(queryClient, SHIPMENTS);
      addSnackbarSuccess({ message: "Successfully Deleted Shipment" });
    },
  });
};
